import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Segmented, Space, Statistic, Table, Tag, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import adminService from '../../../services/adminService';

const currency = (value) => `£${Number(value || 0).toFixed(2)}`;

const HireDurationReportTab = ({ dateRange, selectedCustomerId, isClient = false }) => {
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateScope, setDateScope] = useState('all');

  const params = useMemo(() => {
    const next = {};
    if (dateScope === 'report' && dateRange?.length === 2) {
      next.start_date = dateRange[0];
      next.end_date = dateRange[1];
    }
    if (selectedCustomerId) next.client_id = selectedCustomerId;
    return next;
  }, [dateRange, dateScope, selectedCustomerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await adminService.getHireDurationReport(params);
      setRows(response.data || []);
      setOverview(response.overview || []);
      setTotals(response.totals || {});
    } catch (error) {
      notification.error({
        message: 'Error loading hire duration report',
        description: error.message || 'Unable to load hire duration data'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  const columns = [
    {
      title: 'Client',
      dataIndex: 'client_name',
      width: 180
    },
    {
      title: 'Property',
      key: 'property',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.property_title}</div>
          <div className="text-xs text-gray-500">
            {[
              record.property_key ? `Property ID ${record.property_key}` : null,
              record.apartment_number ? `Flat/Apartment ${record.apartment_number}` : null,
              record.property_address,
              record.postcode
            ].filter(Boolean).join(', ')}
          </div>
        </div>
      )
    },
    {
      title: 'Property ID',
      dataIndex: 'property_key',
      width: 110
    },
    {
      title: 'Order Dates',
      key: 'order_dates',
      render: (_, record) => (
        <Space size={[0, 6]} wrap>
          {(record.orders || []).map((order) => (
            <Tag key={`${record.property_key}-${order.order_id}`}>
              #{order.order_id} {order.order_date}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Days Between Orders',
      key: 'days_between_orders',
      render: (_, record) => {
        const intervals = record.intervals || [];

        if (!intervals.length) {
          return <span className="text-gray-400">-</span>;
        }

        return (
          <Space size={[0, 6]} wrap>
            {intervals.map((interval) => (
              <Tag
                key={`${interval.from_order_id}-${interval.to_order_id}`}
                color={interval.is_overheld ? 'red' : 'default'}
              >
                #{interval.from_order_id} to #{interval.to_order_id}: {interval.days_between} days
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Orders',
      key: 'orders',
      align: 'right',
      render: (_, record) => record.orders?.length || 0
    },
    {
      title: 'Overheld Linen',
      dataIndex: 'overheld_linen',
      align: 'right',
      render: (value) => Number(value || 0)
    },
    {
      title: 'Overheld Orders',
      dataIndex: 'overheld_orders',
      align: 'right',
      render: (value) => Number(value || 0)
    },
  ];

  if (!isClient) {
    columns.push({
      title: 'Total Loss',
      dataIndex: 'loss_total',
      align: 'right',
      render: currency
    });
  }

  const expandedRowRender = (record) => (
    <div>
      <div className="mb-3">
        <div className="text-xs uppercase text-gray-500 mb-2">Order sequence</div>
        <Space size={[0, 6]} wrap>
          {(record.orders || []).map((order) => (
            <Tag key={`order-${record.property_key}-${order.order_id}`}>
              #{order.order_id} / {order.order_date} / linen {Number(order.linen_quantity || 0)}
            </Tag>
          ))}
        </Space>
      </div>
      <Table
        rowKey={(row) => `${row.from_order_id}-${row.to_order_id}`}
        size="small"
        pagination={false}
        dataSource={record.intervals || []}
        columns={[
          { title: 'From Order', dataIndex: 'from_order_id' },
          { title: 'From Date', dataIndex: 'from_date' },
          { title: 'To Order', dataIndex: 'to_order_id' },
          { title: 'To Date', dataIndex: 'to_date' },
          {
            title: 'Days Between',
            dataIndex: 'days_between',
            render: (value, row) => row.is_overheld ? <Tag color="red">{value} days</Tag> : `${value} days`
          },
          {
            title: 'Linen Qty',
            dataIndex: 'linen_quantity',
            align: 'right',
            render: (value) => Number(value || 0)
          }
        ]}
      />
    </div>
  );

  return (
    <div>
      <div className={`grid grid-cols-1 ${isClient ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4 mb-4`}>
        <Card>
          <Statistic title="Total Overheld Linen" value={Number(totals.total_overheld_linen || 0)} />
        </Card>
        <Card>
          <Statistic title="Orders With Overheld Linen" value={Number(totals.orders_with_overheld_linen || 0)} />
        </Card>
        {!isClient && (
          <Card>
            <Statistic title="Total Loss" value={currency(totals.total_loss)} />
          </Card>
        )}
      </div>

      <Card className="mb-4" title="Clients With Most Held Linen">
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={overview.slice(0, 10)} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="client_name" angle={-25} textAnchor="end" interval={0} height={70} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="overheld_linen" name="Overheld linen" fill="#12355b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Space className="mb-4" wrap>
        <Segmented
          value={dateScope}
          onChange={setDateScope}
          options={[
            { label: 'All Dates', value: 'all' },
            { label: 'Report Dates', value: 'report' }
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Refresh
        </Button>
      </Space>

      <Table
        rowKey={(record) => `${record.client_id}-${record.property_key}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        expandable={{ expandedRowRender }}
        rowClassName={(record) => Number(record.overheld_orders || 0) > 0 ? 'bg-red-50' : ''}
        pagination={{ defaultPageSize: 20, pageSizeOptions: ['20', '50', '100', '200'], showSizeChanger: true }}
      />
    </div>
  );
};

export default HireDurationReportTab;
